import { Component, inject, OnInit } from '@angular/core';
import {
    FormBuilder, FormGroup,
    ReactiveFormsModule, Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import { environment } from '../../../environments/environment';
import { login, loginProvider, setLoading } from '../../store/user/user.actions';
import { TranslocoModule } from '@ngneat/transloco';
import {
    RegistrationDashIconComponent
} from '../../components/registration-dash-icon/registration-dash-icon.component';
import { APP_ROUTES } from "../../app.routes.definition";
import { AsyncPipe } from "@angular/common";
import { NativeAPIService } from "../../services/native/native-api.service";
import { selectUserError, selectUserIsLoading } from "../../store/user/user.selectors";
import { LoginForm } from "../../models/formgroups/login-form.model";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

interface TestEmailCredentials {
    index: number;
    label: string;
    mail: string;
    pass: string;
}

@Component({
    selector: 'app-login-page',
    standalone: true,
    imports: [
        RegistrationDashIconComponent,
        TranslocoModule,
        ReactiveFormsModule,
        AsyncPipe,
        RouterLink,
    ],
    templateUrl: './login-page.component.html',
    styleUrl: './login-page.component.css',
})
export default class LoginPageComponent implements OnInit {
    #nativeAPIService = inject(NativeAPIService);
    #activatedRoute = inject(ActivatedRoute);

    readonly loginTestEmailsEnabled = environment.loginTestEmailsEnabled;
    readonly defaultTestEmailIndex = 1;
    testEmails: TestEmailCredentials[] = [];
    #fb = inject(FormBuilder);
    #store = inject(Store<AppState>);
    isLoading$ = this.#store.select(selectUserIsLoading);
    loginError$ = this.#store.select(selectUserError);

    accessTokenLabel = environment.accessTokenKey;
    apiUrl = environment.apiUrl;
    googleAuth = '/google-auth';
    appleAuth = '/apple-auth';
    errorMessage: string | null = null;
    loginForm!: FormGroup<LoginForm>;
    showPassword = false;
    selectedTestEmailIndex = -1;

    authPath = APP_ROUTES.AUTH();
    resetPasswordPath = APP_ROUTES.RESET_PASSWORD();

    constructor() {
        if (this.loginTestEmailsEnabled) {
            this.testEmails = [
                {
                    index: 1,
                    label: 'Giulia Rossi',
                    mail: 'giulia.rossi@gmail.com.bobdev.invalid',
                    pass: 'giulia',
                },
                {
                    index: 0,
                    label: 'Luca Di Sabatino',
                    mail: 'luca.disabatino@seidorgunpowder.com',
                    pass: 'testtest',
                },
                {
                    index: 2,
                    label: 'Claudio Verci',
                    mail: 'verci.claudio@libero.it.bobdev.invalid',
                    pass: 'verci',
                },
                {
                    index: 3,
                    label: 'Utente senza abbonamento',
                    mail: 'norilax938@noomlocs.com',
                    pass: 'norilax938',
                },
                {
                    index: 4,
                    label: 'Input vuoto',
                    mail: '',
                    pass: '',
                },
            ];
        }

        if (this.#nativeAPIService.isAvailable()) {
            this.googleAuth = '/google-native-app-auth';
            this.appleAuth = '/apple-native-app-auth';
        }

        this.initializeForm();

        this.loginError$.pipe(takeUntilDestroyed()).subscribe((value) => {
            if (value != null) {
                if (value.status >= 400 && value.status < 500) {
                    this.errorMessage =
                        'Credenziali non valide. Si prega di riprovare';
                } else if (value.status >= 500) {
                    // Use else if for the second condition
                    this.errorMessage =
                        'Ops, si è verificato un errore, contattare il supporto';
                }
            } else {
                this.errorMessage = '';
            }
        });
    }

    ngOnInit() {
        const accessToken =
            this.#activatedRoute.snapshot.queryParamMap.get('access_token');
        if (accessToken) {
            this.#store.dispatch(loginProvider({ accessToken }));
        }
    }

    get shouldShowTestEmailSelector(): boolean {
        return this.loginTestEmailsEnabled && this.testEmails.length > 0;
    }

    private initializeForm(): void {
        const defaultTestEmail = this.getTestEmailByIndex(this.defaultTestEmailIndex);
        this.selectedTestEmailIndex = defaultTestEmail?.index ?? -1;

        this.loginForm = this.#fb.nonNullable.group({
            email: this.#fb.nonNullable.control(
                defaultTestEmail?.mail ?? '',
                [Validators.required, Validators.email]
            ),
            password: this.#fb.nonNullable.control(
                defaultTestEmail?.pass ?? '',
                [Validators.required]
            ),
        });
    }

    onTestEmailChange(selectedIndex: string): void {
        const testEmail = this.getTestEmailByIndex(Number(selectedIndex));
        if (!testEmail) return;

        this.selectedTestEmailIndex = testEmail.index;
        this.loginForm.patchValue({
            email: testEmail.mail,
            password: testEmail.pass,
        });
        this.loginForm.markAsPristine();
        this.loginForm.markAsUntouched();
        this.errorMessage = '';
    }

    onLoginFormSubmit(): void {
        if (this.loginForm.invalid) return;
        const { email, password } = this.loginForm.value;

        this.#store.dispatch(login({ email: email!, password: password! }));
    }

    togglePassword(): void {
        this.showPassword = !this.showPassword;
    }

    ngOnDestroy() {
        this.#store.dispatch(setLoading({ isLoading: false }));
    }

    private getTestEmailByIndex(index: number): TestEmailCredentials | undefined {
        if (!this.loginTestEmailsEnabled) return undefined;

        return this.testEmails.find((testEmail) => testEmail.index === index);
    }
}
